/**
 * FreightRulesTab — Settings / Pricing → Freight Rules.
 *
 * A rule maps ``(trigger, product_family)`` to:
 *   - a Procurement Catalog item (supplier cost)
 *   - a Sales Rate Card item (customer charge, optional)
 *
 * Prices are NOT stored on the rule. Whenever a rule fires (via the
 * split-delivery detector on order PATCH), the reconciler resolves the
 * live tier price from the procurement item and the current unit price
 * from the sales rate item.
 *
 * The item pickers are searchable comboboxes backed by the flat
 * ``/api/freight-picker/*-items`` endpoints so admins pick from the
 * existing pricing setup — never from a bespoke freight table.
 */
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
    AlertTriangle, Plus, Pencil, Truck, Trash2, Check, X, ChevronsUpDown,
} from 'lucide-react';
import { API_BASE } from '../lib/apiBase';
import CountrySelect from './CountrySelect';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
    Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from './ui/command';
import { cn } from '../lib/utils';

const TRIGGER_LABELS = { split_delivery: 'Split delivery', standard: 'Standard', express: 'Express', manual: 'Manual' };
const FAMILY_LABELS = { vinyl: 'Vinyl', cd: 'CD', dvd: 'DVD', bluray: 'Blu-ray', any: 'Any' };
const QUANTITY_LABELS = {
    per_extra_destination: 'One per extra destination (N-1)',
    flat_one: 'Flat one per order',
    manual: 'Manual only',
};
const INVALID_REASON_LABELS = {
    sales_rate_item_archived: 'Linked Sales Rate Card item is archived',
    sales_rate_item_missing: 'Charge customer is on but no Sales Rate Card item linked',
    procurement_item_archived: 'Linked Procurement item is archived',
};

const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const emptyDraft = () => ({
    name: '',
    trigger: 'split_delivery',
    product_family: 'any',
    procurement_item_id: '',
    sales_rate_item_id: '',
    charge_customer: true,
    included: false,
    quantity_rule: 'per_extra_destination',
    supplier_vendor_default: '',
    country_overrides: [],
    is_active: true,
});

const fmtPrice = (v, currency) =>
    v == null ? '—' : `${currency || 'EUR'} ${Number(v).toFixed(2)}`;

/* ── Searchable combobox for existing pricing items ─────────────────── */
function ItemCombobox({
    value, onChange, options, placeholder, testId, disabled,
    renderRow, filterKeys, emptyLabel,
}) {
    const [open, setOpen] = useState(false);
    const selected = options.find((o) => o.id === value) || null;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    disabled={disabled}
                    className={cn(
                        'w-full justify-between font-normal h-auto min-h-9 py-1.5 text-left',
                        !selected && 'text-gray-500',
                    )}
                    data-testid={testId}
                >
                    <span className="truncate min-w-0">
                        {selected ? renderRow(selected, 'trigger') : placeholder}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[560px] p-0" align="start">
                <Command
                    filter={(itemValue, search) => {
                        // itemValue is what we set on CommandItem `value` — we
                        // encode all searchable fields there (SKU, item name,
                        // catalog / rate card, supplier, category) joined by
                        // " · " plus the item id as a suffix to guarantee
                        // uniqueness (cmdk otherwise dedupes identical values).
                        return itemValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
                    }}
                >
                    <CommandInput placeholder="Search by SKU, name or catalog…" />
                    <CommandList className="max-h-96">
                        <CommandEmpty>{emptyLabel || 'No matches.'}</CommandEmpty>
                        <CommandGroup>
                            {options.map((opt) => {
                                const isSelected = value === opt.id;
                                return (
                                    <CommandItem
                                        key={opt.id}
                                        /* Append id so cmdk never collapses two
                                         * rows that happen to have the same
                                         * SKU/name/catalog string. */
                                        value={`${filterKeys(opt)} · ${opt.id}`}
                                        onSelect={() => {
                                            onChange(opt.id);
                                            setOpen(false);
                                        }}
                                        data-testid={`${testId}-option-${opt.id}`}
                                        className={cn(
                                            'items-start gap-2 rounded-md py-2 px-2 mb-0.5',
                                            /* Override the shadcn Command primitive's dark
                                             * accent hover with a readable light-indigo
                                             * highlight. Selected row gets a soft green tint
                                             * so the current pick stands out even before the
                                             * dropdown scrolls into view. */
                                            'data-[selected=true]:!bg-indigo-50 data-[selected=true]:!text-gray-900',
                                            isSelected && 'bg-emerald-50/70 ring-1 ring-emerald-200',
                                        )}
                                    >
                                        <Check className={cn(
                                            'mr-1 mt-1 h-4 w-4 shrink-0',
                                            isSelected ? 'text-emerald-600 opacity-100' : 'opacity-0',
                                        )} />
                                        <div className="flex-1 min-w-0">{renderRow(opt, 'row')}</div>
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

export default function FreightRulesTab() {
    const [rules, setRules] = useState([]);
    const [procurementItems, setProcurementItems] = useState([]);
    const [salesRateItems, setSalesRateItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogRule, setDialogRule] = useState(null);
    const [draft, setDraft] = useState(emptyDraft());
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [rulesRes, procRes, salesRes] = await Promise.all([
                fetch(`${API_BASE}/api/freight-rules`, { headers: authHeaders() }),
                fetch(`${API_BASE}/api/freight-picker/procurement-items`, { headers: authHeaders() }),
                fetch(`${API_BASE}/api/freight-picker/sales-rate-items`, { headers: authHeaders() }),
            ]);
            if (!rulesRes.ok) throw new Error(`Freight rules: HTTP ${rulesRes.status}`);
            if (!procRes.ok) throw new Error(`Procurement items: HTTP ${procRes.status}`);
            if (!salesRes.ok) throw new Error(`Sales rate items: HTTP ${salesRes.status}`);
            setRules(await rulesRes.json());
            setProcurementItems(await procRes.json());
            setSalesRateItems(await salesRes.json());
        } catch (err) {
            console.error('freight-rules load', err);
            toast.error(err.message || 'Could not load freight rules');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => { setDraft(emptyDraft()); setDialogRule({ id: null }); };
    const openEdit = (rule) => {
        setDraft({
            name: rule.name || '',
            trigger: rule.trigger,
            product_family: rule.product_family,
            procurement_item_id: rule.procurement_item_id,
            sales_rate_item_id: rule.sales_rate_item_id || '',
            charge_customer: !!rule.charge_customer,
            included: !!rule.included,
            quantity_rule: rule.quantity_rule || 'per_extra_destination',
            supplier_vendor_default: rule.supplier_vendor_default || '',
            country_overrides: (rule.country_overrides || []).map((o) => ({
                country_code: o.country_code,
                procurement_item_id: o.procurement_item_id || '',
                sales_rate_item_id: o.sales_rate_item_id || '',
            })),
            is_active: rule.is_active !== false,
        });
        setDialogRule(rule);
    };

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...draft };
            if (payload.included) payload.charge_customer = false;
            if (!payload.sales_rate_item_id) payload.sales_rate_item_id = null;
            if (!payload.supplier_vendor_default) payload.supplier_vendor_default = null;
            // Keep only complete override rows (country + procurement chosen);
            // normalise empty sales to null.
            payload.country_overrides = (draft.country_overrides || [])
                .filter((o) => o.country_code && o.procurement_item_id)
                .map((o) => ({
                    country_code: o.country_code,
                    procurement_item_id: o.procurement_item_id,
                    sales_rate_item_id: o.sales_rate_item_id || null,
                }));
            const codes = payload.country_overrides.map((o) => o.country_code.toUpperCase());
            if (new Set(codes).size !== codes.length) {
                setSaving(false);
                toast.error('Duplicate country in country-specific pricing — each country can appear once per rule');
                return;
            }
            const url = dialogRule?.id
                ? `${API_BASE}/api/freight-rules/${dialogRule.id}`
                : `${API_BASE}/api/freight-rules`;
            const method = dialogRule?.id ? 'PATCH' : 'POST';
            const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(payload) });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || `HTTP ${res.status}`);
            }
            toast.success(dialogRule?.id ? 'Freight rule updated' : 'Freight rule created');
            setDialogRule(null);
            load();
        } catch (err) {
            toast.error(err.message || 'Could not save rule');
        } finally {
            setSaving(false);
        }
    };

    // ── Country-specific pricing row helpers ──────────────────────────
    const addOverrideRow = () => {
        setDraft((d) => ({ ...d, country_overrides: [...(d.country_overrides || []), { country_code: '', procurement_item_id: '', sales_rate_item_id: '' }] }));
    };
    const updateOverrideRow = (idx, patch) => {
        setDraft((d) => {
            const rows = [...(d.country_overrides || [])];
            rows[idx] = { ...rows[idx], ...patch };
            return { ...d, country_overrides: rows };
        });
    };
    const removeOverrideRow = (idx) => {
        setDraft((d) => ({ ...d, country_overrides: (d.country_overrides || []).filter((_, i) => i !== idx) }));
    };


    const archive = async (rule) => {
        if (!window.confirm(`Deactivate rule "${rule.name}"?`)) return;
        try {
            const res = await fetch(`${API_BASE}/api/freight-rules/${rule.id}`, {
                method: 'DELETE', headers: authHeaders(),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            toast.success('Rule deactivated.');
            load();
        } catch (err) {
            toast.error(err.message || 'Could not deactivate');
        }
    };

    /* Product Family is a RULE CONDITION (which order.product_type the
     * rule fires on), NOT a filter on the pricing pickers. The pickers
     * intentionally show every ACTIVE Procurement / Sales Rate Card
     * item across every catalog/rate card so admins can, e.g., pair a
     * "Split delivery / CD" rule with a supplier item from a Vinyl
     * catalog if that's how their pricing is set up. */
    const procurementOptions = procurementItems;
    const salesOptions = salesRateItems;

    /* ── Row renderers ─────────────────────────────────────────────── */
    const renderProcRow = (opt, mode) => {
        const price = fmtPrice(opt.cost_per_unit, opt.catalog_currency);
        if (mode === 'trigger') {
            return (
                <span>
                    <span className="font-mono text-[11px] text-gray-500 mr-1">{opt.supplier_sku}</span>
                    <span className="text-gray-900">{opt.name}</span>
                    <span className="text-gray-500"> — {price}/unit</span>
                </span>
            );
        }
        return (
            <div>
                <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
                        {opt.supplier_sku || '—'}
                    </span>
                    <span className="font-medium text-gray-900 truncate">{opt.name || 'Unnamed item'}</span>
                    {!opt.is_active && (
                        <Badge variant="outline" className="text-[10px] py-0 border-amber-300 text-amber-700">
                            archived
                        </Badge>
                    )}
                </div>
                <div className="text-[11px] text-gray-500 flex items-center gap-1 flex-wrap mt-0.5">
                    <span className="inline-flex items-center gap-1 text-indigo-700">
                        <span className="text-[9px] uppercase tracking-wider text-indigo-500">catalog</span>
                        <span className="font-medium">{opt.catalog_name || '— none —'}</span>
                    </span>
                    {opt.supplier_name && (
                        <>
                            <span className="text-gray-300">·</span>
                            <span>{opt.supplier_name}</span>
                        </>
                    )}
                    <span className="text-gray-300">·</span>
                    <span className="text-gray-700 font-medium">{price}/unit</span>
                </div>
            </div>
        );
    };

    const renderSalesRow = (opt, mode) => {
        const price = fmtPrice(opt.unit_price, opt.rate_card_currency);
        if (mode === 'trigger') {
            return (
                <span>
                    <span className="font-mono text-[11px] text-gray-500 mr-1">{opt.sales_sku}</span>
                    <span className="text-gray-900">{opt.display_name}</span>
                    <span className="text-gray-500"> — {price}/unit</span>
                </span>
            );
        }
        return (
            <div>
                <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
                        {opt.sales_sku || '—'}
                    </span>
                    <span className="font-medium text-gray-900 truncate">
                        {opt.display_name || 'Unnamed item'}
                    </span>
                    {!opt.is_active && (
                        <Badge variant="outline" className="text-[10px] py-0 border-amber-300 text-amber-700">
                            archived
                        </Badge>
                    )}
                </div>
                <div className="text-[11px] text-gray-500 flex items-center gap-1 flex-wrap mt-0.5">
                    <span className="inline-flex items-center gap-1 text-indigo-700">
                        <span className="text-[9px] uppercase tracking-wider text-indigo-500">rate card</span>
                        <span className="font-medium">{opt.rate_card_name || '— none —'}</span>
                    </span>
                    <span className="text-gray-300">·</span>
                    <span className="text-gray-700 font-medium">{price}/unit</span>
                </div>
            </div>
        );
    };

    const procFilterKeys = (o) => [
        o.supplier_sku, o.name, o.catalog_name, o.supplier_name, o.category, o.product_family,
    ].filter(Boolean).join(' · ');

    const salesFilterKeys = (o) => [
        o.sales_sku, o.display_name, o.rate_card_name, o.category, o.product_family,
    ].filter(Boolean).join(' · ');

    return (
        <div className="space-y-4" data-testid="freight-rules-tab">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Truck className="h-5 w-5 text-indigo-600" /> Freight Rules
                    </h2>
                    <p className="text-sm text-gray-500">
                        Link triggers (split delivery, express…) to existing Procurement + Sales Rate Card items.
                        At most one active rule per trigger × product family.
                    </p>
                </div>
                <Button onClick={openCreate} data-testid="freight-rules-add-btn">
                    <Plus className="h-4 w-4 mr-1" /> New rule
                </Button>
            </div>

            <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                        <tr>
                            <th className="px-3 py-2 text-left">Name</th>
                            <th className="px-3 py-2 text-left">Trigger</th>
                            <th className="px-3 py-2 text-left">Family</th>
                            <th className="px-3 py-2 text-left">Procurement item</th>
                            <th className="px-3 py-2 text-left">Sales Rate item</th>
                            <th className="px-3 py-2 text-left">Charge</th>
                            <th className="px-3 py-2 text-left">Qty rule</th>
                            <th className="px-3 py-2 text-left">Active</th>
                            <th className="px-3 py-2 w-8"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100" data-testid="freight-rules-table">
                        {loading && (
                            <tr><td colSpan={9} className="px-3 py-6 text-center text-gray-400">Loading…</td></tr>
                        )}
                        {!loading && rules.length === 0 && (
                            <tr>
                                <td colSpan={9} className="px-3 py-8 text-center text-gray-400" data-testid="freight-rules-empty">
                                    No freight rules yet. Click <strong>New rule</strong> to configure your first mapping.
                                </td>
                            </tr>
                        )}
                        {rules.map((r) => (
                            <tr key={r.id} className={`hover:bg-indigo-50/40 ${r.invalid_reason ? 'bg-amber-50/40' : ''}`}
                                data-testid={`freight-rules-row-${r.id}`}>
                                <td className="px-3 py-2">
                                    <div className="font-medium text-gray-900">{r.name}</div>
                                    {r.invalid_reason && (
                                        <div className="text-[11px] text-amber-700 flex items-center gap-1 mt-0.5">
                                            <AlertTriangle className="h-3 w-3" />
                                            {INVALID_REASON_LABELS[r.invalid_reason] || r.invalid_reason}
                                        </div>
                                    )}
                                </td>
                                <td className="px-3 py-2 text-gray-700">{TRIGGER_LABELS[r.trigger] || r.trigger}</td>
                                <td className="px-3 py-2 text-gray-700">{FAMILY_LABELS[r.product_family] || r.product_family}</td>
                                <td className="px-3 py-2">
                                    <div className="text-gray-900">{r.procurement_item?.name || '—'}</div>
                                    <div className="text-[11px] text-gray-500">
                                        {r.procurement_item?.tiers?.[0]?.cost_per_unit != null
                                            ? `${r.procurement_currency || 'EUR'} ${r.procurement_item.tiers[0].cost_per_unit.toFixed(2)}/unit`
                                            : ''}
                                    </div>
                                </td>
                                <td className="px-3 py-2">
                                    <div className="text-gray-900">
                                        {r.sales_rate_item?.display_name
                                            || r.sales_rate_item?.name
                                            || (r.included ? '—' : (r.charge_customer ? '(missing)' : '—'))}
                                    </div>
                                    <div className="text-[11px] text-gray-500">
                                        {r.sales_rate_item?.tiers?.[0]?.unit_price != null
                                            ? `EUR ${Number(r.sales_rate_item.tiers[0].unit_price).toFixed(2)}/unit`
                                            : ''}
                                    </div>
                                </td>
                                <td className="px-3 py-2">
                                    {r.included
                                        ? <Badge variant="outline" className="bg-slate-50 text-slate-600">Included</Badge>
                                        : r.charge_customer
                                            ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">Yes</Badge>
                                            : <Badge variant="outline">No</Badge>}
                                </td>
                                <td className="px-3 py-2 text-gray-700 text-xs">{QUANTITY_LABELS[r.quantity_rule] || r.quantity_rule}</td>
                                <td className="px-3 py-2">
                                    {r.is_active
                                        ? <Check className="h-4 w-4 text-emerald-600" />
                                        : <X className="h-4 w-4 text-gray-400" />}
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <div className="flex items-center gap-1 justify-end">
                                        <Button variant="ghost" size="icon" className="h-7 w-7"
                                            onClick={() => openEdit(r)} data-testid={`freight-rules-edit-${r.id}`}>
                                            <Pencil className="h-3.5 w-3.5" />
                                        </Button>
                                        {r.is_active && (
                                            <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-500"
                                                onClick={() => archive(r)} data-testid={`freight-rules-archive-${r.id}`}>
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </Button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={!!dialogRule} onOpenChange={(o) => !o && setDialogRule(null)}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="freight-rules-dialog">
                    <DialogHeader>
                        <DialogTitle>{dialogRule?.id ? 'Edit freight rule' : 'New freight rule'}</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-1 gap-3 min-w-0">
                        <div>
                            <Label>Name</Label>
                            <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                                placeholder="e.g. Split delivery — Vinyl"
                                data-testid="freight-rules-name" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Trigger</Label>
                                <Select value={draft.trigger} onValueChange={(v) => setDraft({
                                    ...draft, trigger: v,
                                    quantity_rule: v === 'split_delivery' ? 'per_extra_destination' : draft.quantity_rule,
                                })}>
                                    <SelectTrigger data-testid="freight-rules-trigger"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(TRIGGER_LABELS).map(([k, v]) => (
                                            <SelectItem key={k} value={k}>{v}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Product family</Label>
                                <Select value={draft.product_family} onValueChange={(v) => setDraft({ ...draft, product_family: v })}>
                                    <SelectTrigger data-testid="freight-rules-family"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(FAMILY_LABELS).map(([k, v]) => (
                                            <SelectItem key={k} value={k}>{v}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-[11px] text-gray-500 mt-1">
                                    Applies only when the order&apos;s product type matches. Does NOT filter the pickers below.
                                </p>
                            </div>
                        </div>
                        <div className="border border-gray-200 rounded-md p-3 space-y-3 bg-gray-50/60" data-testid="freight-country-pricing">
                            <div className="flex items-center justify-between">
                                <div>
                                    <Label className="text-sm font-medium">Country-specific pricing</Label>
                                    <p className="text-[11px] text-gray-500 mt-0.5">
                                        Optional. Match by the delivery address country (ISO code). Each country uses its own
                                        Procurement + Sales Rate Card item. Unmatched countries fall back to the generic items below.
                                    </p>
                                </div>
                                <Button type="button" variant="outline" size="sm" onClick={addOverrideRow} data-testid="freight-country-add">
                                    <Plus className="h-3.5 w-3.5 mr-1" /> Add country
                                </Button>
                            </div>
                            {(draft.country_overrides || []).length === 0 ? (
                                <p className="text-[11px] text-gray-400 italic">No country-specific rows. The generic items below apply to all destinations.</p>
                            ) : (
                                <div className="space-y-2">
                                    {(draft.country_overrides || []).map((row, idx) => {
                                        const chosen = (draft.country_overrides || [])
                                            .filter((_, i) => i !== idx)
                                            .map((r) => (r.country_code || '').toUpperCase());
                                        const dup = row.country_code && chosen.includes(row.country_code.toUpperCase());
                                        return (
                                            <div key={idx} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] gap-2 items-start" data-testid={`freight-country-row-${idx}`}>
                                                <div className="min-w-0">
                                                    <CountrySelect
                                                        value={row.country_code}
                                                        onChange={(code) => updateOverrideRow(idx, { country_code: code })}
                                                        testid={`freight-country-select-${idx}`}
                                                        placeholder="Country…"
                                                    />
                                                    {dup && <p className="text-[10px] text-red-600 mt-0.5">Duplicate country</p>}
                                                </div>
                                                <div className="min-w-0">
                                                    <ItemCombobox
                                                        value={row.procurement_item_id}
                                                        onChange={(id) => updateOverrideRow(idx, { procurement_item_id: id })}
                                                        options={procurementOptions}
                                                        placeholder="Procurement item…"
                                                        testId={`freight-country-proc-${idx}`}
                                                        renderRow={renderProcRow}
                                                        filterKeys={procFilterKeys}
                                                        emptyLabel="No procurement items."
                                                    />
                                                </div>
                                                <div className="min-w-0">
                                                    <ItemCombobox
                                                        value={row.sales_rate_item_id}
                                                        onChange={(id) => updateOverrideRow(idx, { sales_rate_item_id: id })}
                                                        options={salesOptions}
                                                        placeholder="Sales item…"
                                                        testId={`freight-country-sales-${idx}`}
                                                        renderRow={renderSalesRow}
                                                        filterKeys={salesFilterKeys}
                                                        emptyLabel="No sales items."
                                                    />
                                                </div>
                                                <Button type="button" variant="ghost" size="icon" onClick={() => removeOverrideRow(idx)} data-testid={`freight-country-remove-${idx}`}>
                                                    <Trash2 className="h-4 w-4 text-red-500" />
                                                </Button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <div className="min-w-0">
                            <Label>Procurement item (supplier cost)</Label>
                            <ItemCombobox
                                value={draft.procurement_item_id}
                                onChange={(id) => setDraft({ ...draft, procurement_item_id: id })}
                                options={procurementOptions}
                                placeholder="Choose an existing Procurement Catalog item…"
                                testId="freight-rules-procurement"
                                renderRow={renderProcRow}
                                filterKeys={procFilterKeys}
                                emptyLabel={
                                    procurementItems.length === 0
                                        ? 'No procurement items loaded.'
                                        : 'No matches for your search.'
                                }
                            />
                            <p className="text-[11px] text-gray-500 mt-1">
                                Reuses your existing Procurement Catalog. Cost tiers come from the item itself.
                            </p>
                        </div>
                        <div className="min-w-0">
                            <Label>Sales Rate Card item (customer charge)</Label>
                            <ItemCombobox
                                value={draft.sales_rate_item_id}
                                onChange={(id) => setDraft({ ...draft, sales_rate_item_id: id })}
                                options={salesOptions}
                                placeholder={draft.included
                                    ? 'Not needed — cost is included'
                                    : (draft.charge_customer
                                        ? 'Choose an existing Sales Rate Card item…'
                                        : 'Not charging customer — leave empty')}
                                testId="freight-rules-sales"
                                renderRow={renderSalesRow}
                                filterKeys={salesFilterKeys}
                                disabled={draft.included || !draft.charge_customer}
                                emptyLabel={
                                    salesRateItems.length === 0
                                        ? 'No sales rate items loaded.'
                                        : 'No matches for your search.'
                                }
                            />
                            <p className="text-[11px] text-gray-500 mt-1">
                                Reuses your existing Sales Rate Cards. The rule stores only a reference — the price
                                comes from the linked item.
                            </p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="flex items-center justify-between border border-gray-200 rounded-md px-3 py-2">
                                <div>
                                    <Label className="mb-0">Charge customer</Label>
                                    <p className="text-[11px] text-gray-500">Adds an auto-add-on to the invoice basis</p>
                                </div>
                                <Switch
                                    checked={draft.charge_customer && !draft.included}
                                    onCheckedChange={(v) => setDraft({ ...draft, charge_customer: v, included: v ? false : draft.included })}
                                    data-testid="freight-rules-charge"
                                />
                            </div>
                            <div className="flex items-center justify-between border border-gray-200 rounded-md px-3 py-2">
                                <div>
                                    <Label className="mb-0">Included</Label>
                                    <p className="text-[11px] text-gray-500">Supplier cost only, no customer charge</p>
                                </div>
                                <Switch
                                    checked={draft.included}
                                    onCheckedChange={(v) => setDraft({ ...draft, included: v, charge_customer: v ? false : draft.charge_customer })}
                                    data-testid="freight-rules-included"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Quantity rule</Label>
                                <Select value={draft.quantity_rule} onValueChange={(v) => setDraft({ ...draft, quantity_rule: v })}>
                                    <SelectTrigger data-testid="freight-rules-qtyrule"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(QUANTITY_LABELS).map(([k, v]) => (
                                            <SelectItem key={k} value={k}>{v}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Supplier vendor default (optional)</Label>
                                <Input value={draft.supplier_vendor_default}
                                    onChange={(e) => setDraft({ ...draft, supplier_vendor_default: e.target.value })}
                                    placeholder="DHL / GLS…" />
                            </div>
                        </div>
                        <div className="flex items-center justify-between border border-gray-200 rounded-md px-3 py-2">
                            <div>
                                <Label className="mb-0">Active</Label>
                                <p className="text-[11px] text-gray-500">Only one active rule per trigger × family</p>
                            </div>
                            <Switch checked={draft.is_active}
                                onCheckedChange={(v) => setDraft({ ...draft, is_active: v })}
                                data-testid="freight-rules-active" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setDialogRule(null)}>Cancel</Button>
                        <Button onClick={save} disabled={saving || !draft.name || !draft.procurement_item_id}
                            data-testid="freight-rules-save">
                            {saving ? 'Saving…' : dialogRule?.id ? 'Save' : 'Create'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
